import React, { useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface GroupedBarChartProps {
    labels: string[];
    datasets: {
        label: string;
        data: number[];
        backgroundColor: string;
    }[];
    title: string;
    onBarClick?: (centro: string) => void;
}

const GroupedBarChart: React.FC<GroupedBarChartProps> = ({ labels, datasets, title, onBarClick }) => {
    const [colors, setColors] = useState({
        textColor: '',
        gridColor: '',
    });

    const chartRef = useRef<any>(null);

    const updateColors = () => {
        const rootStyles = getComputedStyle(document.documentElement);
        setColors({
            textColor: rootStyles.getPropertyValue('--text-color').trim(),
            gridColor: rootStyles.getPropertyValue('--grid-color-chart').trim(),
        });
    };

    useEffect(() => {
        updateColors();

        const observer = new MutationObserver(() => {
            updateColors();
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });

        return () => observer.disconnect();
    }, []);

    // Normalizar labels y registrar para depuración
    const processedLabels: string[] = Array.isArray(labels)
        ? labels
        : labels !== undefined && labels !== null
            ? [String(labels)]
            : [];

    const data = {
        labels: processedLabels,
        datasets: [
            ...datasets.map((dataset, index) => ({
                ...dataset,
                backgroundColor: dataset.backgroundColor || `rgba(${index * 50}, ${100 + index * 30}, ${150 + index * 20}, 0.5)`,
            })),]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    color: colors.textColor,
                },
            },
            title: {
                display: true,
                text: title,
                color: colors.textColor,
            },
            datalabels: {
                display: true
            },
            customValuePlugin: {
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: (title.toLowerCase().includes("tasa") || title.toLowerCase().includes("graduación")) ? 100 : undefined,
                ticks: {
                    color: colors.textColor,
                },
                grid: {
                    color: colors.gridColor,
                },
            },
            x: {
                ticks: {
                    color: colors.textColor,
                    autoSkip: true,
                    maxTicksLimit: processedLabels.length
                },
                grid: {
                    color: colors.gridColor,
                },
            },
        },
        onClick: (event: any, elements: any[]) => {
            if (onBarClick && elements && elements.length > 0) {
                const element = elements[0];
                // element.datasetIndex: índice del dataset (centro)
                // element.index: índice del label (año)
                const centro = datasets[element.datasetIndex]?.label;
                if (centro) {
                    onBarClick(centro);
                }
            }
        }
    };

    const customValuePlugin = {
        id: 'customValuePlugin',
        afterDatasetsDraw: (chart: any) => {
            const { ctx } = chart;
            chart.data.datasets.forEach((dataset: any, i: number) => {
                const meta = chart.getDatasetMeta(i);
                // Si el dataset está oculto, no dibujamos el valor
                if (meta.hidden) return;
                meta.data.forEach((bar: any, index: number) => {
                    const value = dataset.data[index];
                    ctx.save();
                    ctx.fillStyle = 'white';
                    ctx.font = 'bold 16px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(value, bar.x, bar.y + bar.height / 2);
                    ctx.restore();
                });
            });
        },
    };
    return <Bar ref={chartRef} data={data} options={options} plugins={[customValuePlugin]}/>;
};

export default GroupedBarChart;